import { useEffect, useState } from 'react';
import { Button, Select, Text, makeStyles, tokens } from '@fluentui/react-components';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchScopedConfig, putScopedConfig } from '../api/config-scope.api';
import { useAuthStore } from '../store/auth.store';
import { attendanceErrorMessage } from '../utils/attendance-error';
import { useT } from '../i18n';
import type { TerminatedPolicy } from '../api/access.api';

interface Rule { id: string; roles?: string[]; direction?: 'IN' | 'OUT'; action: TerminatedPolicy }
const ROLES = ['GUARD', 'SUPERVISOR', 'HR', 'ADMIN'];
const ACTIONS: TerminatedPolicy[] = ['STRICT', 'VISITOR', 'AUTHORIZE'];
const KEY_RULES = 'attendance.gateTerminatedRules';
const KEY_BASE = 'attendance.gateTerminatedPolicy';

const useStyles = makeStyles({
  card: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS, padding: tokens.spacingVerticalM, border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusMedium },
  row: { display: 'flex', gap: tokens.spacingHorizontalS, flexWrap: 'wrap', alignItems: 'center' },
  muted: { color: tokens.colorNeutralForeground3 },
});

const parse = (raw: unknown): Rule[] => {
  try { const a = JSON.parse(String(raw ?? '[]')); return Array.isArray(a) ? a : []; } catch { return []; }
};

/** Reglas de caseta para personas dadas de baja: lista ordenada (gana la primera que coincide) + política general. */
export function GateRulesEditor() {
  const t = useT();
  const g = t.gatePage;
  const s = useStyles();
  const qc = useQueryClient();
  const tenantId = useAuthStore((st) => st.user?.tenantId);
  const cfg = useQuery({ queryKey: ['tenant-config', tenantId], queryFn: () => fetchScopedConfig('TENANT', tenantId!), enabled: !!tenantId, retry: false });
  const [base, setBase] = useState<TerminatedPolicy>('STRICT');
  const [rules, setRules] = useState<Rule[]>([]);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    if (!cfg.data) return;
    setBase((cfg.data.effective[KEY_BASE]?.value as TerminatedPolicy) ?? 'STRICT');
    setRules(parse(cfg.data.effective[KEY_RULES]?.value));
  }, [cfg.data]);

  const save = useMutation({
    mutationFn: () => putScopedConfig('TENANT', tenantId!, { [KEY_BASE]: base, [KEY_RULES]: JSON.stringify(rules) }),
    onSuccess: () => { setMsg(g.rulesSaved); void qc.invalidateQueries({ queryKey: ['tenant-config', tenantId] }); void qc.invalidateQueries({ queryKey: ['access-config'] }); },
    onError: (e) => setMsg(attendanceErrorMessage(e, t.attendancePage.errors, t.attendancePage.genericError)),
  });

  const patch = (i: number, p: Partial<Rule>) => setRules(rules.map((r, k) => (k === i ? { ...r, ...p } : r)));
  const move = (i: number, d: number) => {
    const j = i + d; if (j < 0 || j >= rules.length) return;
    const next = [...rules]; [next[i], next[j]] = [next[j], next[i]]; setRules(next);
  };
  const label = (a: TerminatedPolicy) => g.policyLabels[a];

  if (!cfg.data) return null;
  return (
    <section className={s.card} aria-label={g.rulesTitle}>
      <Text weight="semibold">{g.rulesTitle}</Text>
      <Text size={200} className={s.muted}>{g.rulesHint}</Text>
      {rules.map((r, i) => (
        <div key={i} className={s.row}>
          <Text>{i + 1}.</Text>
          <Select aria-label={g.ruleRole} value={r.roles?.[0] ?? ''} onChange={(_, d) => patch(i, { roles: d.value ? [d.value] : undefined })}>
            <option value="">{g.anyRole}</option>
            {ROLES.map((x) => <option key={x} value={x}>{x}</option>)}
          </Select>
          <Select aria-label={g.ruleDirection} value={r.direction ?? ''} onChange={(_, d) => patch(i, { direction: (d.value || undefined) as Rule['direction'] })}>
            <option value="">{g.anyDirection}</option>
            <option value="IN">{g.entry}</option>
            <option value="OUT">{g.exit}</option>
          </Select>
          <Select aria-label={g.ruleAction} value={r.action} onChange={(_, d) => patch(i, { action: d.value as TerminatedPolicy })}>
            {ACTIONS.map((x) => <option key={x} value={x}>{label(x)}</option>)}
          </Select>
          <Button size="small" disabled={i === 0} onClick={() => move(i, -1)} aria-label={g.moveUp}>↑</Button>
          <Button size="small" disabled={i === rules.length - 1} onClick={() => move(i, 1)} aria-label={g.moveDown}>↓</Button>
          <Button size="small" onClick={() => setRules(rules.filter((_, k) => k !== i))}>{g.removeRule}</Button>
        </div>
      ))}
      <div className={s.row}>
        <Button size="small" disabled={rules.length >= 20} onClick={() => setRules([...rules, { id: `r${Date.now().toString(36)}`, action: 'STRICT' }])}>{g.addRule}</Button>
        <Text>{g.otherwise}</Text>
        <Select aria-label={g.otherwise} value={base} onChange={(_, d) => setBase(d.value as TerminatedPolicy)}>
          {ACTIONS.map((x) => <option key={x} value={x}>{label(x)}</option>)}
        </Select>
        <Button appearance="primary" disabled={save.isPending} onClick={() => { setMsg(''); save.mutate(); }}>{g.saveRules}</Button>
        {msg && <Text size={200}>{msg}</Text>}
      </div>
    </section>
  );
}
